import type {
  EstateProject,
  EstateProjectGeoJsonPolygon,
  EstateProjectInvestor,
  EstateProjectListParams,
  EstateProjectMedia,
  EstateProjectUpsertPayload,
} from '@/modules/estate-project/types/estate-project-types'

const now = () => new Date().toISOString()

const makeMedia = (uuid: string): EstateProjectMedia => ({
  uuid,
  name: uuid,
  url: {
    thumbnail: '',
    medium: '',
    original: '',
  },
})

let investors: EstateProjectInvestor[] = [
  {
    id: 'inv-001',
    name: 'Công ty CP Đầu tư An Phát',
    logo: 'https://example.com/investor-an-phat.png',
    description: 'Chủ đầu tư tập trung phân khúc căn hộ cao cấp tại TP.HCM.',
  },
  {
    id: 'inv-002',
    name: 'Công ty TNHH Phát triển Minh Thành',
    logo: 'https://example.com/investor-minh-thanh.png',
    description: 'Đơn vị phát triển dự án nhà ở thấp tầng và khu đô thị vệ tinh.',
  },
]

const defaultPolygon: EstateProjectGeoJsonPolygon = {
  type: 'Polygon',
  coordinates: [
    [
      [106.7009, 10.7752],
      [106.7017, 10.7752],
      [106.7017, 10.7745],
      [106.7009, 10.7745],
      [106.7009, 10.7752],
    ],
  ],
}

let projects: EstateProject[] = [
  {
    id: 1,
    project_name: 'Khu căn hộ Riverside One',
    project_address: 'Phường Bến Nghé, Quận 1, TP.HCM',
    lat: 10.7749,
    lng: 106.7012,
    polygon_geojson: defaultPolygon,
    investor: investors[0],
    project_information: [
      { key: 'Phân khu', value: 'A1' },
      { key: 'Diện tích', value: '2.5ha' },
    ],
    description: '<p>Dự án trung tâm quận 1.</p>',
    photos: [makeMedia('seed-photo-1')],
    videos: [makeMedia('seed-video-1')],
    status: 'active',
    created_at: now(),
    updated_at: now(),
    created_by_name: 'Thai Tran',
  },
]

const wait = async () => new Promise((resolve) => setTimeout(resolve, 120))

const contains = (text: string, keyword: string) =>
  text.toLowerCase().includes(keyword.toLowerCase())

export const estateProjectMockAdapter = {
  async listProjects(params: EstateProjectListParams) {
    await wait()

    const keyword = String(params.keyword || '').trim()
    const statuses = params.status || []

    let filtered = [...projects]
    if (keyword) {
      filtered = filtered.filter((item) => {
        return contains(item.project_name, keyword) || contains(item.project_address, keyword)
      })
    }

    if (statuses.length > 0) {
      filtered = filtered.filter((item) => statuses.includes(item.status))
    }

    const page = params.page || 1
    const pageSize = params.pageSize || 10
    const start = (page - 1) * pageSize
    const data = filtered.slice(start, start + pageSize)

    return {
      data,
      total: filtered.length,
      last_page: Math.max(1, Math.ceil(filtered.length / pageSize)),
    }
  },

  async getProject(id: number) {
    await wait()
    const found = projects.find((item) => item.id === id)
    if (!found) throw new Error('Không tìm thấy dự án')
    return found
  },

  async createProject(payload: EstateProjectUpsertPayload) {
    await wait()
    const investor = investors.find((item) => item.id === payload.owner_id) || null

    const id = projects.length > 0 ? Math.max(...projects.map((item) => item.id)) + 1 : 1
    const project: EstateProject = {
      id,
      project_name: payload.name,
      project_address: payload.address,
      lat: payload.lat,
      lng: payload.lng,
      polygon_geojson: payload.geojson,
      investor,
      project_information: payload.information,
      description: payload.description,
      photos: payload.photos.map((uuid) => makeMedia(uuid)),
      videos: payload.videos.map((uuid) => makeMedia(uuid)),
      logo: payload.logo,
      status: 'active',
      created_at: now(),
      updated_at: now(),
      created_by_name: 'Thai Tran',
    }

    projects = [project, ...projects]
    return project
  },

  async updateProject(id: number, payload: EstateProjectUpsertPayload) {
    await wait()

    const index = projects.findIndex((item) => item.id === id)
    if (index < 0) throw new Error('Không tìm thấy dự án')

    const investor = investors.find((item) => item.id === payload.owner_id) || null

    const next: EstateProject = {
      ...projects[index],
      project_name: payload.name,
      project_address: payload.address,
      lat: payload.lat,
      lng: payload.lng,
      polygon_geojson: payload.geojson,
      investor,
      project_information: payload.information,
      description: payload.description,
      photos: payload.photos.map((uuid) => makeMedia(uuid)),
      videos: payload.videos.map((uuid) => makeMedia(uuid)),
      logo: payload.logo,
      updated_at: now(),
    }

    projects[index] = next
    return next
  },

  async deleteProject(id: number) {
    await wait()

    const index = projects.findIndex((item) => item.id === id)
    if (index < 0) throw new Error('Không tìm thấy dự án')

    const deleted = projects[index]
    projects.splice(index, 1)
    return deleted
  },

  async listInvestors(keyword = '') {
    await wait()
    const q = keyword.trim()
    if (!q) return [...investors]
    return investors.filter((item) => contains(item.name, q))
  },

  async createInvestor(input: Pick<EstateProjectInvestor, 'name' | 'logo' | 'description'>) {
    await wait()

    const normalizedName = input.name.trim().toLowerCase()
    const existing = investors.find((item) => item.name.trim().toLowerCase() === normalizedName)
    if (existing) throw new Error('Chủ đầu tư đã tồn tại theo tên')

    const id = `inv-${Date.now()}`
    const investor: EstateProjectInvestor = {
      id,
      name: input.name.trim(),
      logo: input.logo,
      description: input.description,
    }
    investors = [investor, ...investors]
    return investor
  },
}
